import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { useTheme } from '@mui/material/styles';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import NavItem from '../NavItem';
import { SET_MENU } from '../../../../../store/actions';
import useSidebarCollapsed from '../../useSidebarCollapsed';
import { guideLeft, navButtonSx, navIconSx, navLabelSx, navTextSx } from '../navStyles';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { IconChevronDown } from '@tabler/icons';

const NO_AUTO_NAVIGATE = [
  'authentication',
  'eServices',
  'infrastructureServices',
  'certServices',
  'webServices',
  'identity',
  'vmaServices',
  'allOrganizations',
  'network',
  'wiredNetwork',
  'contracts'
];

const NavCollapse = ({ menu, level }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const collapsed = useSidebarCollapsed() && level <= 1;

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const children = Array.isArray(menu?.children) ? menu.children.filter(Boolean) : [];

  const goToFirstChild = () => {
    const firstUrl = children[0]?.url;
    if (!NO_AUTO_NAVIGATE.includes(menu?.id) && firstUrl) {
      navigate(firstUrl);
    }
  };

  const handleClick = () => {
    if (collapsed) {
      dispatch({ type: SET_MENU, opened: true });
      setOpen(true);
      setSelected(menu?.id);
      goToFirstChild();
      return;
    }
    setOpen(!open);
    setSelected(!selected ? menu?.id : null);
    goToFirstChild();
  };

  const { pathname } = useLocation();
  const checkOpenForParent = (child, id) => {
    child.filter(Boolean).forEach((item) => {
      if (item.url === pathname) {
        setOpen(true);
        setSelected(id);
      }
    });
  };

  useEffect(() => {
    setOpen(false);
    setSelected(null);
    children.forEach((item) => {
      if (item.children?.length) {
        checkOpenForParent(item.children, menu?.id);
      }
      if (item.url === pathname) {
        setSelected(menu?.id);
        setOpen(true);
      }
    });

  }, [pathname, menu]);

  const menus = children.map((item) => {
    switch (item.type) {
      case 'collapse':
        return <NavCollapse key={item.id} menu={item} level={level + 1} />;
      case 'item':
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  if (!menu) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('NavCollapse rendered without a "menu" prop – check the menu config / the component that renders it.', { level });
    }
    return null;
  }

  const isSelected = selected === menu.id;

  const Icon = menu.icon;
  const menuIcon = menu.icon ? (
    <Icon strokeWidth={1.8} size={18} />
  ) : (
    <FiberManualRecordIcon sx={{ width: isSelected ? 8 : 6, height: isSelected ? 8 : 6 }} fontSize="inherit" />
  );

  const button = (
    <ListItemButton
      sx={navButtonSx(theme, { level, collapsed, kind: 'group' })}
      selected={isSelected}
      onClick={handleClick}
      aria-expanded={collapsed ? undefined : open}
      aria-label={collapsed ? menu.title : undefined}
    >
      <ListItemIcon sx={navIconSx({ level, collapsed })}>{menuIcon}</ListItemIcon>
      {!collapsed && (
        <>
          <ListItemText
            sx={navTextSx}
            primary={
              <Typography component="span" display="block" sx={navLabelSx(level, isSelected)}>
                {menu.title}
              </Typography>
            }
            secondary={
              menu.caption && (
                <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block">
                  {menu.caption}
                </Typography>
              )
            }
          />
          <IconChevronDown
            stroke={1.8}
            size={16}
            aria-hidden="true"
            style={{
              flexShrink: 0,
              marginLeft: 4,
              color: theme.palette.text.disabled,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .2s ease'
            }}
          />
        </>
      )}
    </ListItemButton>
  );

  return (
    <>
      {collapsed ? (
        <Tooltip title={menu.title} placement="right">
          {button}
        </Tooltip>
      ) : (
        button
      )}
      {!collapsed && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              position: 'relative',
              mb: 0.5,
              '&:after': {
                content: "''",
                position: 'absolute',
                left: `${guideLeft(level)}px`,
                top: 4,
                bottom: 4,
                width: '1px',
                background: theme.palette.divider
              }
            }}
          >
            {menus}
          </List>
        </Collapse>
      )}
    </>
  );
};

NavCollapse.propTypes = {
  menu: PropTypes.object,
  level: PropTypes.number
};

export default NavCollapse;